import React, { useEffect, useState, useRef, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import configData from '../config.json';
import { fetchToken, onForegroundMessage, messaging, isPushSupported } from '../utils/firebase';
import { Modal } from '../components/ui/Modal';
import translations from '../constants/translations.json';
import { useDataContext } from '../context/data';
import {
  getOrderRefund,
  isWoltPushType,
  pushOrderId,
  refundNotice,
  refundNoticeKey,
  woltNoticeKey,
  woltPushNotice,
} from '../utils/woltPush';
import { announceDeliveryChange } from '../utils/deliveryTracking';

// Order news that can end a delivery but never start one.
const TRACKED_ONLY_PUSHES = new Set(['order_completed', 'order_cancelled', 'order_refunded']);

// Whether the news is good: a failed delivery is never shown with the
// celebration icon (the app's BannerNotif variant). No variant — every other
// push — keeps the icon it always had.
const NOTICE_ICONS = {
  failure: {
    emoji: '⚠️',
    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    shadow: '0 4px 16px rgba(239,68,68,0.3)',
  },
  info: {
    emoji: '🛵',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    shadow: '0 4px 16px rgba(59,130,246,0.3)',
  },
};

export const NotificationContext = createContext();

export function NotificationWrapper({ children }) {
  let pathname = window.location.pathname;
  const page = pathname.split('/')?.[1];
  const fromKiosk = page?.includes('kiosk') || false;
  const broadcast = new BroadcastChannel('notif-channel');

  const { language } = useDataContext();

  const [showNotificationInfoModal, setShowNotificationInfoModal] = useState(false);
  const [notificationModal, setNotificatioModal] = useState({
    open: false,
    notification: {},
  });
  const [closePendingModal, setClosePendingModal] = useState(false);
  const [notSupportedPushModal, setNotSupportedPushModal] = useState(false);

  const onPageLoad = async () => {
    const isSupported = await isPushSupported();
    if (!isSupported) {
      return;
    }

    const hasAcceptedWarning = localStorage.getItem('hw-accept-notif-warning');
    if (hasAcceptedWarning) {
      getFCMToken();
    } else {
      setShowNotificationInfoModal(true);
    }
  };

  useEffect(() => {
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, false);
      return () => window.removeEventListener('load', onPageLoad);
    }
  }, []);

  const getFCMToken = async () => {
    try {
      const token = await fetchToken();

      // A guest who declined the push prompt has no token. Posting anyway asked
      // the backend to store nothing, which could only fail its own validation —
      // two "Request failed with status code 500" errors in the console on every
      // page load, for a request with nothing to send.
      if (!token) return;

      let userProfile = localStorage.getItem('user_profile');
      userProfile = JSON.parse(userProfile);

      await axios({
        method: 'POST',
        url: `${configData.SERVER_URL}/user/web/registrationtoken/store`,
        data: {
          registrationtoken: token,
        },
        headers: {
          Authorization: `Bearer ${userProfile?.token || ''}`,
        },
      });
    } catch (ex) {
      console.log('getFCMToken() ', ex.message);
    }
  };

  const dismissModal = () => {
    setShowNotificationInfoModal(false);
    localStorage.setItem('hw-accept-notif-warning', true);
    getFCMToken();
  };

  // Stampcard V2 engagement pushes (one-stamp-to-go, reward ready, voucher
  // expiring) carry data.type 'stamp_engagement' and land the customer on the
  // wallet. Guests (the server's guestCta flag) go there too: the wallet already
  // shows them the save-card conversion banner, so it is the one destination.
  const stampPushData = notificationModal?.notification?.data;
  const isStampPush = stampPushData?.type === 'stamp_engagement';
  const isGuestStampPush = isStampPush && stampPushData?.guestCta === '1';
  const openStampTarget = () => {
    setNotificatioModal({ open: false, notification: {} });
    // This provider sits outside BrowserRouter, so router navigation is not
    // available here; a location assign is the one hop that works from here.
    window.location.assign('/wallet');
  };

  const suppressedNotificationTypes = ['cancelled', 'cancel', 'refunded'];
  const shouldSuppressNotification = (payload) => {
    const body = payload?.notification?.body?.toLowerCase() || '';
    const title = payload?.notification?.title?.toLowerCase() || '';
    const type = payload?.data?.type?.toLowerCase() || '';
    return suppressedNotificationTypes.some(
      (t) => body.includes(t) || title.includes(t) || type.includes(t),
    );
  };

  // Keys of the notices already shown (see utils/woltPush woltNoticeKey).
  // `notice` null only records the key: the caller told the guest another way.
  const shownNoticeKeysRef = useRef(new Set());
  const notifyOnce = useCallback((key, notice) => {
    if (key) {
      if (shownNoticeKeysRef.current.has(key)) return false;
      shownNoticeKeysRef.current.add(key);
    }
    if (notice) setNotificatioModal({ open: true, notification: notice });
    return true;
  }, []);

  // The page tracking an order (useOrderStatusPolling) takes that order's
  // refund push: it ends the wait in the order modal, so the refund is told
  // once — there — and not again in a second modal on top.
  const refundPushHandlersRef = useRef(new Set());
  const onRefundPush = useCallback((handler) => {
    refundPushHandlersRef.current.add(handler);
    return () => refundPushHandlersRef.current.delete(handler);
  }, []);

  // What an order push means, as the app decides it (hey_waitr
  // orders/index.ts onChangeOrderStatus): a refund first — never the Wolt
  // "contact the restaurant" text for it — then a Wolt delivery update in our
  // own copy. Anything else is shown as before.
  const showPush = (payload) => {
    const data = payload?.data;

    // The floating delivery tracker catches up now rather than on its next
    // read (the app's refreshDeliveryForPush / refreshTrackedDelivery).
    if (isWoltPushType(data?.type) || data?.type === 'order_accepted') {
      announceDeliveryChange(pushOrderId(data));
    } else if (TRACKED_ONLY_PUSHES.has(data?.type)) {
      announceDeliveryChange(pushOrderId(data), { trackedOnly: true });
    }

    const refund = getOrderRefund(data);
    if (refund) {
      const key = refundNoticeKey(refund.orderId);
      if (shownNoticeKeysRef.current.has(key)) return;
      for (const handler of refundPushHandlersRef.current) {
        if (handler(refund)) return;
      }
      notifyOnce(key, refundNotice(refund.reason, refund.orderId));
      return;
    }

    if (isWoltPushType(data?.type)) {
      const orderId = pushOrderId(data);
      notifyOnce(woltNoticeKey(data.type, orderId), woltPushNotice(data.type, orderId));
      return;
    }

    if (!shouldSuppressNotification(payload)) {
      setNotificatioModal({ open: true, notification: payload });
    }
  };

  broadcast.onmessage = (event) => {
    console.log('BACKGROUND ', event.data.payload);
    showPush(event.data.payload);
    setClosePendingModal(true);
  };

  // Registered once, as the app's messaging().onMessage is. The one-shot
  // onMessageListener() promise was re-armed on every render, so a push that
  // left this component's state as it was — a suppressed one, a repeat, a
  // refund the order page took — left no listener for the next push.
  const showPushRef = useRef(showPush);
  showPushRef.current = showPush;
  useEffect(() => {
    try {
      return onForegroundMessage((payload) => {
        console.log('FOREGROUND ', payload);
        showPushRef.current(payload);
        setClosePendingModal(true);
      });
    } catch (err) {
      console.log('failed: ', err);
      return undefined;
    }
  }, []);

  const notice = notificationModal?.notification;
  const noticeIcon = NOTICE_ICONS[notice?.variant];
  // A notice we wrote names its copy; a push brings its own text.
  const noticeText = notice?.messageKey
    ? translations[language]?.[notice.messageKey] || translations.en[notice.messageKey]
    : notice?.notification?.body;

  return (
    <NotificationContext.Provider
      value={{
        onPageLoad,
        closePendingModal,
        setClosePendingModal,
        setNotificatioModal,
        notificationModal,
        notifyOnce,
        onRefundPush,
      }}
    >
      {children}

      {!fromKiosk && (
        <>
          {/* Enable push notif warning */}
          <Modal
            isOpen={showNotificationInfoModal}
            noCloseButton={true}
            contentClassName="max-w-[420px]"
          >
            <div className="text-center pt-8 px-6 pb-6">
              <div
                className="w-[72px] h-[72px] rounded-full flex items-center justify-center mx-auto mb-5 shadow-[0_4px_16px_rgba(150,193,31,0.3)]"
                style={{ background: 'linear-gradient(135deg, #96c11f 0%, #7ba819 100%)' }}
              >
                <span className="text-3xl">🔔</span>
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">
                {translations[language]?.notification_modal_push_title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto mb-6">
                {translations[language]?.notification_modal_push_message}
              </p>
              <button
                className="w-full py-[14px] px-6 text-[15px] font-semibold bg-primary hover:bg-secondary text-white border-none rounded-xl cursor-pointer transition-colors duration-200 shadow-[0_2px_8px_rgba(150,193,31,0.3)]"
                onClick={dismissModal}
              >
                {translations[language]?.notification_modal_push_button}
              </button>
              <button
                className="w-full py-3 px-6 text-[13px] font-medium bg-transparent text-gray-400 border-none cursor-pointer hover:underline mt-2"
                onClick={dismissModal}
              >
                {translations[language]?.notification_modal_push_skip}
              </button>
            </div>
          </Modal>

          {/* Push not supported */}
          <Modal
            isOpen={notSupportedPushModal}
            noCloseButton={true}
            contentClassName="max-w-[600px]"
          >
            <div className="text-center p-4">
              <h4 className="text-lg mb-6">
                {translations[language]?.notification_modal_push_unsupported_message}
              </h4>
              <button
                className="bg-primary hover:bg-secondary text-white font-medium px-6 py-2 rounded-full transition-colors"
                onClick={() => {
                  setNotSupportedPushModal(false);
                  localStorage.setItem('hw-accept-not-supported-push', true);
                }}
              >
                {translations[language]?.notification_modal_push_unsupported_button}
              </button>
            </div>
          </Modal>

          {/* Notification modal handler */}
          <Modal
            isOpen={notificationModal.open}
            onClose={() => setNotificatioModal({ open: false, notification: {} })}
            contentClassName="max-w-[420px]"
          >
            <div className="pt-8 px-6 pb-6">
              <div className="text-center mb-5">
                <div
                  className="w-[72px] h-[72px] rounded-full flex items-center justify-center mx-auto mb-5 shadow-[0_4px_16px_rgba(150,193,31,0.3)]"
                  style={
                    noticeIcon
                      ? { background: noticeIcon.background, boxShadow: noticeIcon.shadow }
                      : { background: 'linear-gradient(135deg, #96c11f, #7ba819)' }
                  }
                >
                  <span className="text-3xl">{noticeIcon ? noticeIcon.emoji : '🎉'}</span>
                </div>
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-2 leading-snug">
                  {noticeText}
                </h3>
                {notificationModal?.notification?.data?.comment && (
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {notificationModal?.notification?.data?.comment}
                  </p>
                )}
              </div>
              {isStampPush && (
                <button
                  onClick={openStampTarget}
                  className="w-full py-[14px] mb-2 text-[15px] font-semibold bg-primary hover:bg-secondary text-white border-none rounded-xl cursor-pointer transition-colors duration-200 shadow-[0_2px_8px_rgba(150,193,31,0.3)]"
                >
                  {isGuestStampPush
                    ? translations[language]?.notification_stamp_save_card || 'Save my stamp card'
                    : translations[language]?.notification_stamp_view_wallet || 'View my stamps'}
                </button>
              )}
              <button
                onClick={() => setNotificatioModal({ open: false, notification: {} })}
                className="w-full py-[14px] text-[15px] font-semibold bg-[#1a1a1a] hover:bg-[#333] text-white border-none rounded-xl cursor-pointer transition-colors duration-200"
              >
                {translations[language]?.notification_modal_confirmation_button}
              </button>
            </div>
          </Modal>
        </>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
}
